"use client";

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Cookies from 'js-cookie';
import { initializeAnalytics, trackPageView } from '../analytics';
import { initPostHog, syncPostHogConsent, capturePageView as posthogPageView } from '../analytics/posthog';
import {
  initLinkedInInsight,
  setLinkedInUserData,
  trackLinkedInConversion,
  LINKEDIN_CONVERSIONS,
} from '../analytics/linkedin';
import { getCookieConsent } from '../contexts/CookieConsentContext';

const getPageTitle = (path) => {
  // Filter out API routes - don't track these
  if (path.startsWith('/api/') || path.startsWith('/_next/') || path.startsWith('/static/')) {
    return null;
  }

  // Map paths to page titles for better analytics
  const titleMap = {
    // Main pages
    '/': 'HomePage',
    '/dashboard': 'Dashboard',
    '/assets': 'Assets',
    '/income': 'Income',
    '/expenses': 'Expenses',
    '/scenarios': 'Scenarios',
    '/settings': 'Settings',
    '/pricing': 'Pricing',
    '/subscription-settings': 'Subscription Settings',
    '/login': 'Login',
    '/cashflow': 'Cashflow',
    '/simulations': 'Simulations',
    '/transactions': 'Transactions',
    
    // Static pages
    '/privacy': 'Privacy Policy',
    '/terms': 'Terms of Service',
    '/security': 'Security',
    '/cookies': 'Cookies Policy',
    '/glossary': 'Glossary',
    '/why-delphina-exists': 'Why Delphina Exists',
    '/features': 'Features',
    '/demo/dashboard': 'Demo Dashboard',
    '/comparison': 'Comparisons',
    '/am-i-on-track': 'Am I On Track',
    
    // Guides
    '/guides': 'Financial Guides',
    '/guides/what-is-fire': 'Guide: What is FIRE',
    '/guides/how-to-budget': 'Guide: How to Budget',
    '/guides/how-to-start-investing': 'Guide: How to Start Investing',
    '/guides/how-to-track-your-net-worth': 'Guide: How to Track Your Net Worth',
    '/guides/can-i-afford-x': 'Guide: Can I Afford X',
    '/guides/personal-finance-tips': 'Guide: Personal Finance Tips',
    '/guides/what-is-a-cash-flow-forecast': 'Guide: What is Cash Flow Forecast',
    '/guides/what-is-financial-guidance': 'Guide: What is Financial Guidance',
    '/guides/how-do-i-save-more': 'Guide: How Do I Save More',
    '/guides/what-is-my-risk-tolerance': 'Guide: What is My Risk Tolerance',
    '/guides/what-does-r-g-mean': 'Guide: What does r > g Mean',
    '/guides/different-withdrawal-methods-explained': 'Guide: Different Withdrawal Methods',
    '/guides/what-is-the-4-percent-rule': 'Guide: What is the 4% Rule',
    '/guides/what-is-guyton-klinger': 'Guide: What is Guyton-Klinger',
    '/guides/what-is-cape-withdrawal': 'Guide: What is CAPE Withdrawal',
    '/guides/what-is-variable-percentage-withdrawal': 'Guide: What is VPW',
    '/guides/cash-savings': 'Guide: Cash Savings & Emergency Funds',
    '/guides/what-is-a-ssas': 'Guide: What is a SSAS',
    '/guides/how-to-plan-financially-for-maternity-leave': 'Guide: Maternity Leave Planning',
    '/guides/moving-house-budgeting': 'Guide: Moving House Budgeting',
    '/guides/cash-flow-forecasting-irregular-incomes': 'Guide: Irregular Income Forecasting',
    '/guides/forecast-vs-budget-vs-cash-flow': 'Guide: Forecast vs Budget vs Cash Flow',
    '/guides/how-much-can-i-safely-spend': 'Guide: How Much Can I Safely Spend',
    '/guides/what-is-an-mmf': 'Guide: What is an MMF',
    '/guides/what-is-an-isa': 'Guide: What is an ISA',
    '/guides/what-is-a-sipp': 'Guide: What is a SIPP',
    '/guides/uk-tax-planning': 'Guide: UK Tax Planning',
    '/guides/how-much-do-i-need-to-retire': 'Guide: How Much Do I Need to Retire',
    
    // Help pages
    '/help': 'Help',
    '/help/getting-started': 'Help: Getting Started',
    '/help/account-management': 'Help: Account Management',
    '/help/flow-chart': 'Help: Flow Chart',
    '/help/coaching-session': 'Help: Coaching Session',
    '/help/contact-support': 'Help: Contact Support',
    '/help/bug-report': 'Help: Bug Report',
    '/help/feature-request': 'Help: Feature Request',
    '/help/faq': 'Help: FAQ',
    '/help/global-market-trackers': 'Help: Global Market Trackers',
    
    // Supported banks
    
    // Auth routes (for completeness, though may not track)
    '/auth/callback': 'Auth: Callback',
    '/auth/error': 'Auth: Error',
  };

  // Check static map first
  if (titleMap[path]) {
    return titleMap[path];
  }

  // Handle comparison pages: /comparison/[slug]-vs-delphina
  if (path.startsWith('/comparison/')) {
    const slug = path.replace('/comparison/', '').replace('-vs-delphina', '').replace(/-/g, ' ');
    return `Comparison: ${slug.charAt(0).toUpperCase() + slug.slice(1)} vs Delphina`;
  }

  // Handle guide pages: /guides/[slug]
  if (path.startsWith('/guides/')) {
    const slug = path.replace('/guides/', '').replace(/-/g, ' ');
    const title = slug.charAt(0).toUpperCase() + slug.slice(1);
    return `Guide: ${title}`;
  }

  // Handle help pages: /help/[slug]
  if (path.startsWith('/help/')) {
    const slug = path.replace('/help/', '').replace(/-/g, ' ');
    const title = slug.charAt(0).toUpperCase() + slug.slice(1);
    return `Help: ${title}`;
  }

  // Handle blog pages: /blog/[slug]
  if (path.startsWith('/blog/')) {
    const slug = path.replace('/blog/', '').replace(/-/g, ' ');
    const title = slug.charAt(0).toUpperCase() + slug.slice(1);
    return `Blog: ${title}`;
  }

  // Handle features pages: /features/[slug]
  if (path.startsWith('/features/')) {
    const slug = path.replace('/features/', '').replace(/-/g, ' ');
    const title = slug.charAt(0).toUpperCase() + slug.slice(1);
    return `Feature: ${title}`;
  }

  // Handle scenario routes: /scenarios/[id]
  if (path.match(/^\/scenarios\/[a-zA-Z0-9-]+$/)) {
    return 'Scenario: Financial Planning';
  }

  // Handle portfolio routes: /portfolio/[id]
  if (path.match(/^\/portfolio\/[a-zA-Z0-9-]+$/)) {
    return 'Portfolio: View';
  }

  // Handle admin routes: /admin/[page]
  if (path.startsWith('/admin/')) {
    const page = path.replace('/admin/', '').replace(/-/g, ' ');
    return `Admin: ${page.charAt(0).toUpperCase() + page.slice(1)}`;
  }

  // Intelligent fallback - extract meaningful information from path
  const segments = path.split('/').filter(Boolean);
  if (segments.length > 0) {
    const lastSegment = segments[segments.length - 1];
    const formatted = lastSegment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return `${formatted} Page`;
  }

  return 'Unknown Page';
};

/**
 * Read the logged-in user identity from the same cookie/localStorage that
 * AuthContext writes. Mirrors `readUserFromCookie` in client/src/utils/analytics.ts
 * so we don't need to thread AuthContext into the analytics boundary.
 *
 * Returns `{ id, email, created_at }` or null if no user is logged in.
 * Resilient to JSON parse errors and missing fields.
 */
const readUserFromCookie = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    // localStorage may throw in private mode - fall through to cookie.
  }
  try {
    const cookieRaw = Cookies.get('user');
    if (cookieRaw) {
      const parsed = JSON.parse(cookieRaw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    // cookie parse failed - fall through.
  }
  return null;
};

const AnalyticsTracker = ({ children }) => {
  const pathname = usePathname();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Check analytics consent and initialize accordingly
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[GA4] AnalyticsTracker effect running, isInitialized:', isInitialized);
    }

    const checkConsent = () => {
      const hasConsent = getCookieConsent('analytics');

      // PostHog runs for decliners too, cookieless. It handles its own
      // granted/declined/undecided states and switches between them.
      syncPostHogConsent();

      if (hasConsent && !isInitialized) {
        // Initialize analytics only if consent is given and not already initialized.
        // The closure captures `isInitialized` by value, so this branch can fire
        // multiple times before the state updates. initPostHog() and
        // initializeAnalytics() are themselves idempotent (`isPostHogInitialized`
        // and the GA4 trackers' own init flag), so re-entry is safe.
        initializeAnalytics();
        initPostHog();
        setIsInitialized(true);
        setAnalyticsEnabled(true);

        if (process.env.NODE_ENV === 'development') {
          console.log('Analytics initialized with user consent');
        }
      } else if (!hasConsent && isInitialized) {
        // Analytics was previously enabled but consent was revoked
        setAnalyticsEnabled(false);

        if (process.env.NODE_ENV === 'development') {
          console.log('Analytics disabled due to lack of consent');
        }
      } else if (hasConsent && isInitialized) {
        // Analytics already initialized and consent still valid
        setAnalyticsEnabled(true);
      }

      // LinkedIn Insight is gated on `marketing` consent (not analytics),
      // so it lives outside the GA/PostHog branch and self-checks consent
      // on each poll. Idempotent - safe to call repeatedly.
      initLinkedInInsight();

      // DEL-254: pass user_id + hashed email to LinkedIn for advanced
      // matching. Runs on every consent poll so login/logout transitions
      // pick up the new identity within ~5s without an extra subscription.
      // setLinkedInUserData is itself idempotent and consent-gated.
      const user = readUserFromCookie();
      if (user && (user.id || user.email)) {
        setLinkedInUserData(user.id, user.email);
      }
    };

    checkConsent();

    // Listen for consent changes
    const handleConsentChange = () => {
      checkConsent();
    };

    // Listen for storage changes (consent updates from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'delphina_cookie_consent') {
        handleConsentChange();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check consent periodically (in case of in-tab changes).
    // DEL-1020: poll every 5s instead of 1s. The 1s interval generated
    // `checkConsent called` log noise in dev mode and a tighter window
    // for handler races when StrictMode mounts this effect twice. 5s
    // still catches in-tab consent changes (cookie writes don't fire
    // `storage` events in the same tab) within an acceptable UX window.
    const interval = setInterval(handleConsentChange, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [isInitialized]);

  // Track page views only when analytics is enabled
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[GA4] Page view effect running, analyticsEnabled:', analyticsEnabled, 'pathname:', pathname);
    }

    if (analyticsEnabled && pathname) {
      const title = getPageTitle(pathname);

      // Skip tracking for API routes and other non-page routes
      if (title === null) {
        return;
      }

      trackPageView(pathname, title);
      posthogPageView(pathname, title);

      // DEL-254: Fire the LinkedIn pricing-page-view conversion on every
      // /pricing navigation. Required by the DEL-241 LinkedIn ad test so
      // the Campaign Manager can attribute trial starts to a paid click.
      // trackLinkedInConversion is itself consent-gated on `marketing`,
      // so this is a no-op if the user has not accepted marketing cookies.
      if (pathname === '/pricing') {
        trackLinkedInConversion(LINKEDIN_CONVERSIONS.PRICING_PAGE_VIEW);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`Page tracked: ${pathname} - ${title}`);
      }
    }
  }, [pathname, analyticsEnabled]);

  return children;
};

export default AnalyticsTracker;
